/**
 * Policies — every API route, as one plain typed function (no hooks).
 * Contract: spec/backend-design.md §9 (routes) and lib/policies/types.ts (shapes).
 *
 * HumanFirewall is a human-risk-management and security-awareness-training platform;
 * the Policies module puts an organisation's own policy PDFs in front of its own staff,
 * who read, tick, answer and acknowledge, and keeps each acknowledgment as evidence.
 *
 * COMPONENTS NEVER CALL THIS FILE. The TanStack hooks live beside it: hooks.ts (the admin
 * session) and portal-hooks.ts (the employee portal and the signed link). They own the
 * query keys, the normalising and the invalidation.
 *
 * Three realms, three transports:
 *  - admin  (/v1/policies/*):        `fetchApi` (the admin session + x-workspace-id).
 *    Multipart, file and CSV calls go through `authorisedFetch` (same session, one refresh
 *    on a 401, no JSON Content-Type). CSV and PDF exports are written in the requester's
 *    language (`lang`).
 *  - portal (/v1/portal/policies/*):  `fetchPortalApi` (the portal JWT, never the admin's);
 *    the PDF through a raw fetch with the portal bearer.
 *  - signed (/v1/public/policies/:attestationId/:sig/*): `fetchApi` with `skipAuth` (no
 *    bearer, and a 401 never touches the admin session).
 *
 * Every refusal arrives as ApiClientError with `.errorCode` (a PolicyErrorCode, or the
 * platform's 403 `{ error: 'module_disabled' }`, isModuleDisabledError). Screens word it
 * through lib/policies/errors.ts and never show `.message`.
 *
 * FROZEN after the contract phase: need another route or a different shape? Ask the lead.
 */
import { API_CONFIG } from '@/lib/api/config'
import { ApiClientError, buildQueryString, fetchApi, getAccessToken, getWorkspaceId, refreshAccessToken } from '@/lib/api/client'
import { portalAuthApi } from '@/lib/api/portal-auth'
import { fetchPortalApi } from '@/lib/api/portal-fetch'
import { filenameFromContentDisposition } from '@/lib/http/content-disposition'
import { fetchAuditorVersionFile } from './audit-api'
import { isPracticeDoor, type PolicyPdfBytes, type PolicyPdfSource, type PolicyPracticeDoor, type PolicyTaskDoor } from './doors'
import { isPolicyLanguage } from './types'
import type {
  AskPolicyAgainBody,
  AssignPolicyBody,
  ChangePolicyDueDateBody,
  ExtendPolicyBody,
  PolicyActivityLogPage,
  PolicyActivityLogQuery,
  PolicyActivitySeries,
  PolicyActivitySeriesQuery,
  PolicyAiRunResult,
  PolicyAiSuggestion,
  PolicyAiSuggestionsQuery,
  PolicyApprovalsCount,
  PolicyApproverRow,
  PolicyApproveResult,
  PolicyArchivedPage,
  PolicyArchivedQuery,
  PolicyArchivePreview,
  PolicyAskAgainResult,
  PolicyAssignmentRow,
  PolicyAssignResult,
  PolicyAttemptsResetResult,
  PolicyAttestationStatusResult,
  PolicyAudiencePreview,
  PolicyContentDiff,
  PolicyCreated,
  PolicyDashboard,
  PolicyDetail,
  PolicyDraftPage,
  PolicyDraftsQuery,
  PolicyDueChangeResult,
  PolicyEmailSwitches,
  PolicyEvidenceRecord,
  PolicyEvidenceVerification,
  PolicyExtendResult,
  PolicyFileUploadResult,
  PolicyIssuesResult,
  PolicyLanguage,
  PolicyLateJoiners,
  PolicyInbox,
  PolicyLibraryPage,
  PolicyLibraryQuery,
  PolicyLibrarySection,
  PolicyPageSummary,
  PolicyPageText,
  PolicyPeoplePage,
  PolicyPeopleQuery,
  PolicyProgress,
  PolicyReminderSettings,
  PolicyRemedialOption,
  PolicyRemindResult,
  PolicyDiagnosis,
  PolicyRequestChangesResult,
  PolicyVersionReview,
  PolicyRoleRow,
  PolicySubmitResult,
  PolicyVersionDetail,
  PolicyVersionRow,
  PolicyWithdrawResult,
  PolicyPracticeAttemptBody,
  PolicyPracticeGradeBody,
  PolicyPracticeReceipt,
  PolicyPracticeReceiptBody,
  PolicyTestFinishedBody,
  PortalPolicyAcknowledgeBody,
  PortalPolicyAcknowledgeResult,
  PortalPolicyAnswersBody,
  PortalPolicySavedAnswers,
  PortalPolicyAttempt,
  PortalPolicyAttemptResult,
  PortalPolicyKeyPointBody,
  PortalPolicyKeyPointsResult,
  PortalPolicyList,
  PortalPolicyOpenBody,
  PortalPolicyStepResult,
  PortalPolicyPageRead,
  PortalPolicyPageReadBody,
  PortalPolicyPageStart,
  PortalPolicyPageStartBody,
  PortalPolicyReceipt,
  PortalPolicyTask,
  RemindPolicyBody,
  RemovePolicyRecipientBody,
  RequestPolicyChangesBody,
  RunPolicyAiBody,
  SubmitPolicyVersionBody,
  UpdatePolicyBody,
  UpdatePolicyRemindersBody,
  UpdatePolicyVersionBody,
} from './types'

// ── Paths ───────────────────────────────────────────────────────────────────

const enc = encodeURIComponent

/**
 * The employee portal's API prefix, a constant on purpose: these are ENDPOINTS, and a page address spelled
 * as a literal template here (".../receipts/${id}") would read to app/__tests__/every-page-has-a-way-in.guard
 * as a link to the portal's receipt page, which that guard lists as opened through a helper.
 */
const PORTAL_API = '/portal/policies'

export const policyPaths = {
  approvalsCount: '/policies/approvals/count',
  inbox: '/policies/library/inbox',
  library: '/policies/library',
  libraryExport: '/policies/library/export.csv',
  policy: (policyId: string) => `/policies/library/${enc(policyId)}`,
  versions: (policyId: string) => `/policies/library/${enc(policyId)}/versions`,
  version: (policyId: string, versionId: string) => `/policies/library/${enc(policyId)}/versions/${enc(versionId)}`,
  roles: '/policies/roles',
  approvers: '/policies/approvers',
  remedialOptions: '/policies/options/remedial',
  emailSwitches: '/policies/options/emails',
  dashboard: '/policies/dashboard',
  evidenceVerify: '/policies/evidence/verify',
  aiDismiss: (suggestionId: string) => `/policies/ai/suggestions/${enc(suggestionId)}/dismiss`,
  receiptPdf: (evidenceId: string) => `/policies/evidence/${enc(evidenceId)}/receipt.pdf`,
  portal: PORTAL_API,
  portalTask: (attestationId: string) => `${PORTAL_API}/tasks/${enc(attestationId)}`,
  portalReceipt: (evidenceId: string) => `${PORTAL_API}/receipts/${enc(evidenceId)}`,
  signed: (attestationId: string, sig: string) => `/public/policies/${enc(attestationId)}/${enc(sig)}`,
  /** v3 (C1): the admin's "Preview as employee" of one version; its stateless calls hang off it (/attempts, /grade, /receipt). */
  previewTask: (policyId: string, versionId: string) => `/policies/library/${enc(policyId)}/versions/${enc(versionId)}/preview`,
  /** v3 (C1, H4): the PDF of a language pack, read by the admin session (the second file of a preview). */
  packFile: (policyId: string, packId: string) => `/policies/library/${enc(policyId)}/languages/${enc(packId)}/file`,
  /** v3 (C2): a test link's task; the signature is the credential. */
  testTask: (testId: string, sig: string) => `/public/policy-tests/${enc(testId)}/${enc(sig)}`,
} as const

/** Query string without the leading '?' (the key part of a query key). */
export function qsOf(params: object | undefined): string {
  return buildQueryString(params as Record<string, unknown> | undefined).replace(/^\?/, '')
}

function withQs(path: string, qs: string): string {
  return qs ? `${path}?${qs}` : path
}

const json = (method: 'POST' | 'PUT' | 'PATCH' | 'DELETE', body?: unknown): RequestInit => ({
  method,
  ...(body === undefined ? {} : { body: JSON.stringify(body) }),
})

// ── Transport for files, multipart and CSV ──────────────────────────────────

const apiUrl = (path: string) => `${API_CONFIG.baseUrl}${API_CONFIG.versionPrefix}${path}`

async function throwFor(res: Response): Promise<never> {
  let body: unknown
  try {
    body = await res.json()
  } catch {
    body = undefined
  }
  throw new ApiClientError(res.status, res.statusText, body)
}

/**
 * Admin session + workspace header, one refresh on a 401 (lib/knowledge/api.ts precedent). Exported for the language
 * packs' multipart upload and PDF (lib/policies/language-pack-api.ts), so the module has one admin file transport.
 */
export async function authorisedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers: Record<string, string> = {}
  const token = getAccessToken()
  if (token) headers.Authorization = `Bearer ${token}`
  const workspaceId = getWorkspaceId()
  if (workspaceId) headers['x-workspace-id'] = workspaceId
  let res = await fetch(apiUrl(path), { ...init, headers })
  if (res.status === 401) {
    const refreshed = await refreshAccessToken()
    if (refreshed) {
      headers.Authorization = `Bearer ${refreshed}`
      res = await fetch(apiUrl(path), { ...init, headers })
    }
  }
  if (!res.ok) await throwFor(res)
  return res
}

function portalHeaders(): Record<string, string> {
  const token = portalAuthApi.getToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export interface PolicyFileBlob {
  blob: Blob
  mime: string
  fileName: string
}

async function blobOf(res: Response, fallbackName: string): Promise<PolicyFileBlob> {
  const blob = await res.blob()
  const mime = (res.headers.get('content-type') || blob.type || '').split(';')[0].trim().toLowerCase()
  return { blob, mime, fileName: filenameFromContentDisposition(res.headers.get('content-disposition')) ?? fallbackName }
}

/**
 * The language a CSV or PDF export is written in: the requester's interface language,
 * English when that is not one of the four (CLAUDE.md rule 9; the survey exports'
 * precedent). Sent as `lang`. Names, e-mails, ids, hashes and ISO instants are never
 * translated.
 */
export function policyExportLanguage(uiLocale: string | null | undefined): PolicyLanguage {
  const value = (uiLocale ?? '').slice(0, 2).toLowerCase()
  return isPolicyLanguage(value) ? value : 'en'
}

/** A CSV export's query: the screen's filters, every page, in the requester's language. */
function exportQs(query: object, uiLocale: string | null | undefined): string {
  return qsOf({ ...query, page: undefined, limit: undefined, lang: policyExportLanguage(uiLocale) })
}

/** Save a blob under its file name (Firefox ignores a click on a detached anchor). */
export function savePolicyBlob(file: PolicyFileBlob): void {
  const href = URL.createObjectURL(file.blob)
  const a = document.createElement('a')
  a.href = href
  a.download = file.fileName
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(href), 30_000)
}

// ═════════════════════════════════════════════════════════════════════════════
// Admin — /v1/policies
// ═════════════════════════════════════════════════════════════════════════════

export const policyApi = {
  // ── Library and navigation (R1-R5, W11-W13a, R7, W8-W9, R24, R27, O1) ──
  approvalsCount: () => fetchApi<PolicyApprovalsCount>(policyPaths.approvalsCount),
  inbox: () => fetchApi<PolicyInbox>(policyPaths.inbox),
  listPublished: (query: PolicyLibraryQuery) =>
    fetchApi<PolicyLibraryPage>(withQs(policyPaths.library, qsOf({ ...query, section: 'published' }))),
  listDrafts: (query: PolicyDraftsQuery) =>
    fetchApi<PolicyDraftPage>(withQs(policyPaths.library, qsOf({ ...query, section: 'drafts' }))),
  listArchived: (query: PolicyArchivedQuery) =>
    fetchApi<PolicyArchivedPage>(withQs(policyPaths.library, qsOf({ ...query, section: 'archived' }))),
  createPolicy: () => fetchApi<PolicyCreated>(policyPaths.library, json('POST', {})),
  clonePolicy: (policyId: string) => fetchApi<PolicyCreated>(`${policyPaths.policy(policyId)}/clone`, json('POST', {})),
  updatePolicy: (policyId: string, body: UpdatePolicyBody) => fetchApi<PolicyDetail>(policyPaths.policy(policyId), json('PATCH', body)),
  deletePolicy: (policyId: string) => fetchApi<void>(policyPaths.policy(policyId), json('DELETE')),
  getPolicy: (policyId: string) => fetchApi<PolicyDetail>(policyPaths.policy(policyId)),
  archivePreview: (policyId: string) => fetchApi<PolicyArchivePreview>(`${policyPaths.policy(policyId)}/archive/preview`),
  roles: () => fetchApi<PolicyRoleRow[]>(policyPaths.roles),
  approvers: (excludeSelf: boolean) => fetchApi<PolicyApproverRow[]>(withQs(policyPaths.approvers, qsOf({ excludeSelf }))),
  remedialOptions: () => fetchApi<PolicyRemedialOption[]>(policyPaths.remedialOptions),
  emailSwitches: () => fetchApi<PolicyEmailSwitches>(policyPaths.emailSwitches),

  // ── Versions, content, approval (R14, W12, R21b, W14, W13, R22p, R22t, R29, W22, R30, W19-W23) ──
  listVersions: (policyId: string) => fetchApi<PolicyVersionRow[]>(policyPaths.versions(policyId)),
  getVersion: (policyId: string, versionId: string) => fetchApi<PolicyVersionDetail>(policyPaths.version(policyId, versionId)),
  updateVersion: (policyId: string, versionId: string, body: UpdatePolicyVersionBody) =>
    fetchApi<PolicyVersionDetail>(policyPaths.version(policyId, versionId), json('PATCH', body)),
  deleteVersion: (policyId: string, versionId: string) => fetchApi<void>(policyPaths.version(policyId, versionId), json('DELETE')),
  versionPages: (policyId: string, versionId: string) =>
    fetchApi<PolicyPageSummary[]>(`${policyPaths.version(policyId, versionId)}/pages`),
  versionPage: (policyId: string, versionId: string, pageNo: number) =>
    fetchApi<PolicyPageText>(`${policyPaths.version(policyId, versionId)}/pages/${pageNo}`),
  versionIssues: (policyId: string, versionId: string) =>
    fetchApi<PolicyIssuesResult>(`${policyPaths.version(policyId, versionId)}/issues`),
  versionReview: (policyId: string, versionId: string) => fetchApi<PolicyVersionReview>(`${policyPaths.version(policyId, versionId)}/review`),
  /** The diff of `versionId` against `withVersionId` (what the newer one changed). */
  versionCompare: (policyId: string, versionId: string, withVersionId: string) =>
    fetchApi<PolicyContentDiff>(withQs(`${policyPaths.version(policyId, versionId)}/compare`, qsOf({ with: withVersionId }))),
  submitVersion: (policyId: string, versionId: string, body: SubmitPolicyVersionBody) =>
    fetchApi<PolicySubmitResult>(`${policyPaths.version(policyId, versionId)}/submit`, json('POST', body)),
  withdrawVersion: (policyId: string, versionId: string) =>
    fetchApi<PolicyWithdrawResult>(`${policyPaths.version(policyId, versionId)}/withdraw`, json('POST', {})),
  requestChanges: (policyId: string, versionId: string, body: RequestPolicyChangesBody) =>
    fetchApi<PolicyRequestChangesResult>(`${policyPaths.version(policyId, versionId)}/request-changes`, json('POST', body)),
  approveVersion: (policyId: string, versionId: string) =>
    fetchApi<PolicyApproveResult>(`${policyPaths.version(policyId, versionId)}/approve`, json('POST', {})),
  restoreFile: (policyId: string, versionId: string) =>
    fetchApi<PolicyVersionDetail>(`${policyPaths.version(policyId, versionId)}/file/restore`, json('POST', {})),

  // ── AI hooks (W17, R31, W18) ──
  runAi: (policyId: string, versionId: string, body: RunPolicyAiBody) =>
    fetchApi<PolicyAiRunResult>(`${policyPaths.version(policyId, versionId)}/ai/runs`, json('POST', body)),
  aiSuggestions: (policyId: string, versionId: string, query: PolicyAiSuggestionsQuery) =>
    fetchApi<PolicyAiSuggestion[]>(withQs(`${policyPaths.version(policyId, versionId)}/ai/suggestions`, qsOf(query))),
  dismissAiSuggestion: (suggestionId: string) => fetchApi<PolicyAiSuggestion>(policyPaths.aiDismiss(suggestionId), json('POST', {})),

  // ── Policy page: figures, people, assignments (R8, R9, R18, R15, R16, W7, W6, R21, W10, W24, R33, W1-W5) ──
  progress: (policyId: string, round: number | null) =>
    fetchApi<PolicyProgress>(withQs(`${policyPaths.policy(policyId)}/progress`, qsOf({ round }))),
  people: (policyId: string, query: PolicyPeopleQuery) =>
    fetchApi<PolicyPeoplePage>(withQs(`${policyPaths.policy(policyId)}/people`, qsOf(query))),
  person: (policyId: string, attestationId: string) =>
    fetchApi<PolicyEvidenceRecord>(`${policyPaths.policy(policyId)}/people/${enc(attestationId)}`),
  assignments: (policyId: string, round: number | null) =>
    fetchApi<PolicyAssignmentRow[]>(withQs(`${policyPaths.policy(policyId)}/assignments`, qsOf({ round }))),
  reminders: (policyId: string) => fetchApi<PolicyReminderSettings>(`${policyPaths.policy(policyId)}/reminders`),
  updateReminders: (policyId: string, body: UpdatePolicyRemindersBody) =>
    fetchApi<PolicyReminderSettings>(`${policyPaths.policy(policyId)}/reminders`, json('PATCH', body)),
  changeDueDate: (policyId: string, body: ChangePolicyDueDateBody) =>
    fetchApi<PolicyDueChangeResult>(`${policyPaths.policy(policyId)}/due-date`, json('PATCH', body)),
  previewAssignment: (policyId: string, body: AssignPolicyBody) =>
    fetchApi<PolicyAudiencePreview>(`${policyPaths.policy(policyId)}/assignments/preview`, json('POST', body)),
  assign: (policyId: string, body: AssignPolicyBody) =>
    fetchApi<PolicyAssignResult>(`${policyPaths.policy(policyId)}/assignments`, json('POST', body)),
  askAgain: (policyId: string, body: AskPolicyAgainBody) =>
    fetchApi<PolicyAskAgainResult>(`${policyPaths.policy(policyId)}/ask-again`, json('POST', body)),
  lateJoiners: (policyId: string) => fetchApi<PolicyLateJoiners>(`${policyPaths.policy(policyId)}/late-joiners`),
  remind: (policyId: string, body: RemindPolicyBody) =>
    fetchApi<PolicyRemindResult>(`${policyPaths.policy(policyId)}/remind`, json('POST', body)),
  extend: (policyId: string, body: ExtendPolicyBody) =>
    fetchApi<PolicyExtendResult>(`${policyPaths.policy(policyId)}/people/extend`, json('POST', body)),
  resetAttempts: (policyId: string, attestationId: string) =>
    fetchApi<PolicyAttemptsResetResult>(`${policyPaths.policy(policyId)}/people/${enc(attestationId)}/reset-attempts`, json('POST', {})),
  removeRecipient: (policyId: string, attestationId: string, body: RemovePolicyRecipientBody) =>
    fetchApi<PolicyAttestationStatusResult>(`${policyPaths.policy(policyId)}/people/${enc(attestationId)}/remove`, json('POST', body)),
  restoreRecipient: (policyId: string, attestationId: string) =>
    fetchApi<PolicyAttestationStatusResult>(`${policyPaths.policy(policyId)}/people/${enc(attestationId)}/restore`, json('POST', {})),

  // ── Reports, activity, dashboard (R13, R11, R12, R25, R32) ──
  reports: (policyId: string, round: number | null) =>
    fetchApi<PolicyDiagnosis>(withQs(`${policyPaths.policy(policyId)}/reports`, qsOf({ round }))),
  activitySeries: (policyId: string, query: PolicyActivitySeriesQuery) =>
    fetchApi<PolicyActivitySeries>(withQs(`${policyPaths.policy(policyId)}/activity/series`, qsOf(query))),
  activityLog: (policyId: string, query: PolicyActivityLogQuery) =>
    fetchApi<PolicyActivityLogPage>(withQs(`${policyPaths.policy(policyId)}/activity/log`, qsOf(query))),
  dashboard: (department: string | null) =>
    fetchApi<PolicyDashboard>(withQs(policyPaths.dashboard, qsOf({ department }))),
  verifyEvidence: () => fetchApi<PolicyEvidenceVerification>(policyPaths.evidenceVerify),
}

// ── Admin files: upload, PDF, CSV, evidence ─────────────────────────────────

/**
 * Multipart PUT of the policy PDF (field `file`, ≤ 25 MB, ≤ 60 pages), with the ids as
 * arguments: the editor uploads before any hook could bind them. The ONE authorised upload.
 * A refusal (`policy_file_not_pdf`, `_too_large`, `_too_many_pages`, `_no_text`,
 * `_unreadable`, `_same_as_current`) keeps the previous file.
 */
export async function uploadPolicyFile(policyId: string, versionId: string, file: File): Promise<PolicyFileUploadResult> {
  const form = new FormData()
  form.append('file', file, file.name)
  const res = await authorisedFetch(`${policyPaths.version(policyId, versionId)}/file`, { method: 'PUT', body: form })
  return (await res.json()) as PolicyFileUploadResult
}

/** GET …/versions/:versionId/file — the stored PDF (admin), as a blob (`download` = Content-Disposition attachment). */
export async function fetchPolicyVersionFile(policyId: string, versionId: string, download = false): Promise<PolicyFileBlob> {
  const res = await authorisedFetch(withQs(`${policyPaths.version(policyId, versionId)}/file`, qsOf({ download: download ? 1 : undefined })))
  return blobOf(res, 'policy.pdf')
}

/** CSV (UTF-8 with a byte-order mark) of one library tab as filtered, written in `uiLocale`. */
export async function exportPolicyLibraryCsv(section: PolicyLibrarySection, query: object, uiLocale: string): Promise<void> {
  const res = await authorisedFetch(withQs(policyPaths.libraryExport, exportQs({ ...query, section }, uiLocale)))
  savePolicyBlob(await blobOf(res, 'policy-library.csv'))
}

/** CSV of a policy's recipients as filtered (removed people and leavers included), written in `uiLocale`. */
export async function exportPolicyPeopleCsv(policyId: string, query: PolicyPeopleQuery, uiLocale: string): Promise<void> {
  const res = await authorisedFetch(withQs(`${policyPaths.policy(policyId)}/people/export.csv`, exportQs(query, uiLocale)))
  savePolicyBlob(await blobOf(res, 'policy-recipients.csv'))
}

/** The evidence PDF of one person's row: the receipt plus the full timeline (read:PolicyEvidence). */
export async function downloadPolicyEvidencePdf(policyId: string, attestationId: string, uiLocale: string): Promise<void> {
  const res = await authorisedFetch(withQs(`${policyPaths.policy(policyId)}/people/${enc(attestationId)}/evidence.pdf`, qsOf({ lang: policyExportLanguage(uiLocale) })))
  savePolicyBlob(await blobOf(res, 'policy-evidence.pdf'))
}

/** The receipt PDF of one acknowledgment (read:PolicyEvidence). `evidenceId` = PortalPolicyReceipt.evidenceId. */
export async function downloadPolicyReceiptPdf(evidenceId: string, uiLocale: string): Promise<void> {
  const res = await authorisedFetch(withQs(policyPaths.receiptPdf(evidenceId), qsOf({ lang: policyExportLanguage(uiLocale) })))
  savePolicyBlob(await blobOf(res, 'policy-receipt.pdf'))
}

// ═════════════════════════════════════════════════════════════════════════════
// Portal and signed link — one task behind either door
// ═════════════════════════════════════════════════════════════════════════════

/** The portal's list of the person's policies (PS R1). `locale` words nothing server-side but the statement. */
export const portalPolicyApi = {
  list: (locale: string | null) => fetchPortalApi<PortalPolicyList>(withQs(policyPaths.portal, qsOf({ locale }))),
  receipt: (evidenceId: string, locale: string | null) =>
    fetchPortalApi<PortalPolicyReceipt>(withQs(policyPaths.portalReceipt(evidenceId), qsOf({ locale }))),
}

/** Where a door's task lives, and how its calls are sent. */
function taskPath(door: PolicyTaskDoor, sub = ''): string {
  switch (door.kind) {
    case 'portal':
      return `${policyPaths.portalTask(door.attestationId)}${sub}`
    case 'signed':
      return `${policyPaths.signed(door.attestationId, door.sig)}${sub}`
    case 'preview':
      return `${policyPaths.previewTask(door.policyId, door.versionId)}${sub}`
    case 'test':
      return `${policyPaths.testTask(door.testId, door.sig)}${sub}`
  }
}

/**
 * The portal door sends the portal bearer; the preview door the ADMIN session (+ x-workspace-id: the maker is
 * looking at their own workspace's version); the signed link and the test link nothing (the signature is the
 * credential, and a 401 there never touches the admin session).
 */
function taskFetch<T>(door: PolicyTaskDoor, sub: string, init?: RequestInit): Promise<T> {
  if (door.kind === 'portal') return fetchPortalApi<T>(taskPath(door, sub), init)
  if (door.kind === 'preview') return fetchApi<T>(taskPath(door, sub), init)
  return fetchApi<T>(taskPath(door, sub), { ...init, skipAuth: true })
}

/**
 * Every call of one policy task, identical behind the portal door and the signed-link door
 * (backend §9.4, §9.5). Reads record nothing; the writes append to the person's timeline.
 */
export const policyTaskApi = {
  /** PS R2: the whole task — pages with their text, key points, questions, the statement. */
  get: (door: PolicyTaskDoor, locale: string | null) =>
    taskFetch<PortalPolicyTask>(door, withQs('', qsOf({ locale, language: door.kind === 'preview' ? door.language : undefined }))),
  open: (door: PolicyTaskDoor, body: PortalPolicyOpenBody) => taskFetch<PortalPolicyStepResult>(door, '/open', json('POST', body)),
  startPage: (door: PolicyTaskDoor, pageNo: number, body: PortalPolicyPageStartBody) =>
    taskFetch<PortalPolicyPageStart>(door, `/pages/${pageNo}/start`, json('POST', body)),
  readPage: (door: PolicyTaskDoor, pageNo: number, body: PortalPolicyPageReadBody) =>
    taskFetch<PortalPolicyPageRead>(door, `/pages/${pageNo}/read`, json('POST', body)),
  setKeyPoint: (door: PolicyTaskDoor, keyPointId: string, body: PortalPolicyKeyPointBody) =>
    taskFetch<PortalPolicyKeyPointsResult>(door, `/key-points/${enc(keyPointId)}`, json('PUT', body)),
  startAttempt: (door: PolicyTaskDoor) => taskFetch<PortalPolicyAttempt>(door, '/attempts', json('POST', {})),
  saveAnswers: (door: PolicyTaskDoor, attemptId: string, body: PortalPolicyAnswersBody) =>
    taskFetch<PortalPolicySavedAnswers>(door, `/attempts/${enc(attemptId)}/answers`, json('PUT', body)),
  submitAttempt: (door: PolicyTaskDoor, attemptId: string, body: PortalPolicyAnswersBody) =>
    taskFetch<PortalPolicyAttemptResult>(door, `/attempts/${enc(attemptId)}/submit`, json('POST', body)),
  acknowledge: (door: PolicyTaskDoor, body: PortalPolicyAcknowledgeBody) =>
    taskFetch<PortalPolicyAcknowledgeResult>(door, '/acknowledge', json('POST', body)),
  /** The receipt behind the signed door (by the task); the portal door reads it by `evidenceId` (portalPolicyApi.receipt). */
  signedReceipt: (attestationId: string, sig: string, locale: string | null) =>
    fetchApi<PortalPolicyReceipt>(withQs(`${policyPaths.signed(attestationId, sig)}/receipt`, qsOf({ locale })), { skipAuth: true }),
}

/**
 * v3 (C1, C2): the STATELESS calls of a practice walk. Nothing is stored by any of them: the attempt is shuffled
 * and the answers graded by the very same code the employee's flow runs (policy-answer-order.ts), and the receipt
 * is a SAMPLE marked PREVIEW or TEST. Only a test link also stamps its own test-run row (`opened`, `finished`).
 */
export const policyPracticeApi = {
  attempt: (door: PolicyPracticeDoor, body: PolicyPracticeAttemptBody) => taskFetch<PortalPolicyAttempt>(door, '/attempts', json('POST', body)),
  grade: (door: PolicyPracticeDoor, body: PolicyPracticeGradeBody) => taskFetch<PortalPolicyAttemptResult>(door, '/grade', json('POST', body)),
  receipt: (door: PolicyPracticeDoor, body: PolicyPracticeReceiptBody) => taskFetch<PolicyPracticeReceipt>(door, '/receipt', json('POST', body)),
  /** A test link only: the first time the link is opened (204). */
  opened: (door: PolicyPracticeDoor) => taskFetch<void>(door, '/opened', json('POST', {})),
  /** A test link only: the walk reached the end (204), with the score of the questions when there were any. */
  finished: (door: PolicyPracticeDoor, body: PolicyTestFinishedBody) => taskFetch<void>(door, '/finished', json('POST', body)),
}

/** The receipt PDF behind a door (the person's own copy: bundled fonts, workspace-zone times). */
export async function downloadPolicyDoorReceiptPdf(door: PolicyTaskDoor, evidenceId: string | null, uiLocale: string): Promise<void> {
  // A practice walk has a sample receipt on screen and no PDF: nothing was recorded.
  if (isPracticeDoor(door)) throw new ApiClientError(404, 'Not Found', { errorCode: 'policy_receipt_not_found' })
  const qs = qsOf({ locale: policyExportLanguage(uiLocale) })
  if (door.kind === 'portal') {
    if (!evidenceId) throw new ApiClientError(404, 'Not Found', { errorCode: 'policy_receipt_not_found' })
    const res = await fetch(apiUrl(withQs(`${policyPaths.portalReceipt(evidenceId)}/pdf`, qs)), { headers: portalHeaders() })
    if (!res.ok) await throwFor(res)
    savePolicyBlob(await blobOf(res, 'policy-receipt.pdf'))
    return
  }
  const res = await fetch(apiUrl(withQs(`${policyPaths.signed(door.attestationId, door.sig)}/receipt/pdf`, qs)))
  if (!res.ok) await throwFor(res)
  savePolicyBlob(await blobOf(res, 'policy-receipt.pdf'))
}

/**
 * The bytes of a policy PDF, from whichever place the source names, with the auth that place
 * needs (admin session + workspace header and one refresh; the portal bearer; nothing for a
 * signed link). The renderer (components/policies/pdf) reads these, checks the SHA-256 the API
 * gave and hands them to pdf.js; nothing here is cached (the renderer caches the parsed document).
 * Reading the file stamps nothing: opening is POST …/open, reading is the per-page events.
 */
export async function fetchPolicyPdfBytes(source: PolicyPdfSource, signal?: AbortSignal): Promise<PolicyPdfBytes> {
  if (source.kind === 'auditor') {
    // v3 (G3): the auditor's own door (X-Policy-Audit-Token, credentials omitted); never the admin session.
    const file = await fetchAuditorVersionFile(source.policyId, source.versionId)
    return { bytes: await file.blob.arrayBuffer(), fileName: file.fileName }
  }
  let res: Response
  if (source.kind === 'admin') {
    res = await authorisedFetch(`${policyPaths.version(source.policyId, source.versionId)}/file`, { signal })
  } else if (source.kind === 'preview') {
    // The previewed version's own file: the admin's file route, the same bytes the maker uploaded.
    res = await authorisedFetch(`${policyPaths.version(source.policyId, source.versionId)}/file`, { signal })
  } else if (source.kind === 'adminPack') {
    res = await authorisedFetch(policyPaths.packFile(source.policyId, source.packId), { signal })
  } else if (source.kind === 'second') {
    // v3 (H4): the translated file beside the base one. Behind a preview it is the pack's admin file; behind
    // the other doors it is the door's own `file?language=` route, with the door's own credentials.
    const door = source.door
    if (door.kind === 'preview') {
      res = await authorisedFetch(policyPaths.packFile(door.policyId, source.packId), { signal })
    } else {
      const query = `?${qsOf({ language: source.language })}`
      const path =
        door.kind === 'portal'
          ? policyPaths.portalTask(door.attestationId)
          : door.kind === 'signed'
            ? policyPaths.signed(door.attestationId, door.sig)
            : policyPaths.testTask(door.testId, door.sig)
      res = await fetch(apiUrl(`${path}/file${query}`), door.kind === 'portal' ? { headers: portalHeaders(), signal } : { signal })
      if (!res.ok) await throwFor(res)
    }
  } else if (source.kind === 'portal') {
    res = await fetch(apiUrl(`${policyPaths.portalTask(source.attestationId)}/file`), { headers: portalHeaders(), signal })
    if (!res.ok) await throwFor(res)
  } else if (source.kind === 'test') {
    res = await fetch(apiUrl(`${policyPaths.testTask(source.testId, source.sig)}/file`), { signal })
    if (!res.ok) await throwFor(res)
  } else {
    res = await fetch(apiUrl(`${policyPaths.signed(source.attestationId, source.sig)}/file`), { signal })
    if (!res.ok) await throwFor(res)
  }
  const fileName = filenameFromContentDisposition(res.headers.get('content-disposition')) ?? 'policy.pdf'
  return { bytes: await res.arrayBuffer(), fileName }
}
